<template>
  <div
    id="vendor-card"
    class="tw-bg-[#FCFCFC] tw-h-[119px] tw-w-[100%] tw-border tw-p-5 tw-border-solid tw-border-[#EFF0F1] tw-rounded-[6px]"
  >
    <div class="tw-flex tw-flex-col tw-h-full tw-justify-between">
      <div class="tw-flex align-items-end">
        <div class="detail-info">
          <div
            class="tw-text-[#211F58] tw-text-[13px] tw-font-[500] tw-leading-[130%] tw-not-italic"
          >
            <div
              class="tw-flex tw-justify-between tw-items-center tw-text-[15px] tw-font-[600] tw-leading-[130%] tw-not-italic"
            >
              <span class="tw-w-[85px]">
                {{
                  getComponentUiText(
                    "app-component-assessments-vendor-card-title-1",
                  )
                }}
              </span>
              <div
                v-if="isLoading"
                class="custom-global-skeleton tw-w-[50%] tw-h-[16px]"
              ></div>
              <div
                v-else
                class="tw-text-[#231F20] tw-text-[13px] tw-overflow-hidden tw-flex-1"
              >
                <p
                  id="vendor-card-name"
                  class="vendor-name"
                  data-cy="vendor-name"
                >
                  {{ vendor?.name || "-" }}
                </p>
              </div>
            </div>
            <div
              class="tw-flex tw-justify-between tw-items-center tw-font-[500] tw-my-[11px]"
            >
              <span class="tw-w-[85px]">
                {{
                  getComponentUiText(
                    "app-component-assessments-vendor-card-recipient-tag-1",
                  )
                }}
              </span>

              <div
                v-if="isLoading"
                class="custom-global-skeleton tw-w-[50%] tw-h-[16px]"
              ></div>
              <div v-else class="tw-overflow-hidden tw-flex-1">
                <p
                  id="vendor-card-email"
                  class="tw-text-[#878787] tw-font-[400] vendor-email"
                  data-cy="recipient-email"
                  @mouseover="onHoverRecipient"
                  @mouseleave="isHover = false"
                >
                  {{ isHover ? recipientName : vendor?.recipient || "-" }}
                </p>
              </div>
            </div>
            <div
              class="tw-flex tw-justify-between tw-items-center tw-font-[500]"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-vendor-card-expiry-date-tag-1",
                )
              }}
              <div
                v-if="isLoading"
                class="custom-global-skeleton tw-w-[50%] tw-h-[16px]"
              ></div>
              <span
                v-else
                id="vendor-card-expiry-date"
                class="tw-text-[#878787] tw-font-[400] tw-cursor-pointer"
                data-cy="expiry-date"
                @mouseover="isHoverExpiryDate = true"
                @mouseleave="isHoverExpiryDate = false"
                >{{ getCurrentTime() }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getComponentUiText, getNextDay } from "@/utils/utilities";
import { getLocale } from "@/utils/dateTimeUtils";
import dayjs from "dayjs";
import { computed, ref } from "vue";

const props = defineProps({
  vendor: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const isHoverExpiryDate = ref(false);
const isHover = ref(false);

const recipientName = computed(() => {
  const recipientNameArr: string[] = [
    props.vendor.recipientFirstName,
    props.vendor.recipientLastName,
  ];
  const recipientNameArrFilter = recipientNameArr.filter(Boolean);
  const result = recipientNameArrFilter.length
    ? recipientNameArrFilter.join(" ")
    : "-";
  return result;
});

const getCurrentTime = () => {
  if (!props.vendor.expiryDate) return "-";
  return isHoverExpiryDate.value
    ? dayjs(props.vendor.expiryDate).locale(getLocale()).format("DD MMM YYYY")
    : getNextDay(new Date(props.vendor.expiryDate));
};

const onHoverRecipient = () => {
  if (recipientName.value === "-") return;
  isHover.value = true;
};
</script>

<style scoped>
.detail-info {
  flex: 1;
  font-family: Poppins;
  width: 100%;
}
.default {
  background: var(--ui-white, #fff);
  color: var(--ui-dark-grey, #878787);
  border-radius: 4px;
  border: 1px solid var(--ui-rule, #eff0f1);
}

.inProgress {
  background: linear-gradient(
      0deg,
      rgba(72, 160, 255, 0.15) 0%,
      rgba(72, 160, 255, 0.15) 100%
    ),
    #fff;
  color: #387dff;
}

.notStarted {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}

.complete {
  background: var(--ui-green-soft, #d9ede2);
  color: #01893d;
}

.audited {
  background: linear-gradient(
      0deg,
      rgba(144, 75, 214, 0.15) 0%,
      rgba(144, 75, 214, 0.15) 100%
    ),
    #fff;
  color: #904bd6;
}
.vendor-email {
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: right;
  margin: 0;
}
.vendor-name {
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: right;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  font-weight: 600;
}
</style>
